// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { fontConfig } from "@eisinc/XDL/PreStyle/typography";
import { xBaseTheme } from "@eisinc/XDL/types";
import color from "@eisinc/XDL/Utilities/color";
import { RadioStyle } from "@insite/mobius/Radio";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { css } from "styled-components";

const generalStyles = css`
    & + label {
        margin-left: 0;
        margin-right: 6px;
        align-items: start;
        color: ${color("common.ui6")};
    }
    & + label::before {
        flex: 1 0 14px;
        width: 14px;
        height: 14px;
        border-radius: 100%;
        border: 1px solid ${color("common.ui4")};
        background-color: ${color("common.white")};
        margin-right: 6px;
        margin-top: 0;
        transition: all 0.3s ease;
    }
    & + label::after {
        width: 6px;
        height: 6px;
        left: 4px;
        top: 4px;
        border-radius: 100%;
        background-color: ${color("common.white")};
        transform: scale(0);
        transition: all 0.3s ease;
    }
    &:checked + label::after {
        transform: scale(1);
    }
    &:focus + label::before {
        box-shadow: none;
    }
`;

const hoverStyle = css`
    &:hover:not(:disabled) + label::before {
        border-color: ${color("common.ui8")};
    }
    &:hover:not(:disabled) + label {
        cursor: pointer;
    }
    &:hover:checked:not(:disabled) + label::before {
        border-color: ${color("common.ui8")};
        background-color: ${color("common.ui8")};
    }
`;

const checkedStyle = css`
    &:checked + label::before {
        border-color: ${color("common.ui9")};
        background-color: ${color("common.ui9")};
    }
`;

const disabledStyle = css`
    &:disabled + label::before,
    &:disabled + label::after {
        background: ${color("common.ui2", 0.33)};
        border: 1px solid ${color("common.ui4", 0.33)};
        cursor: not-allowed;
    }
`;

// xng-changes: tab variant – add an 8px padded border around the label that
// mirrors the radio's border color across states (inactive, hover, selected, disabled)
const tabVariantStyle = css`
    &[data-variant="tab"] + label {
        display: inline-flex;
        align-items: center;
        padding: 7px;
        border: 1px solid ${color("common.ui4")};
        border-radius: 0;
        transition: all 0.3s ease;
        margin-bottom: 16px;
    }

    &[data-variant="tab"] + label::after {
        left: 11px;
        top: 11px;
    }

    // Hover state (unchecked)
    &[data-variant="tab"]:hover:not(:disabled) + label {
        border-color: ${color("common.ui8")};
    }

    // Checked state
    &[data-variant="tab"]:checked + label {
        border-color: ${color("common.ui9")};
        background-color: ${color("common.ui2")};
    }

    // Checked + hover
    &[data-variant="tab"]:hover:checked:not(:disabled) + label {
        border-color: ${color("common.ui8")};
    }

    // Disabled
    &[data-variant="tab"]:disabled + label {
        border-color: ${color("common.ui4", 0.33)};
        cursor: not-allowed;
    }
`;

export default {
    defaultProps: {
        mergeCss: true,
        typographyProps: {
            mergeCss: true,
            ...fontConfig.singleLinexSmall,
            weight: fontConfig.weight.regular,
            color: "text.main",
            css: css`
                letter-spacing: 0;
                user-select: none;
            `,
        },
        css: css`
            &&&& {
                input {
                    margin-left: 0px;
                    margin-right: 0px;
                    margin-top: 0px;
                    width: 13px;
                    height: 13px;
                    display: none;
                    ${generalStyles}
                    ${hoverStyle}
                    ${checkedStyle}
                    ${disabledStyle}
                    ${tabVariantStyle}
                }
            }
        `,
    },
    groupDefaultProps: {
        mergeCss: true,
        labelProps: {
            ...fontConfig.singleLineRegular,
            weight: fontConfig.weight.medium,
            css: css`
                letter-spacing: 0;
            `,
        },
        errorProps: {
            weight: fontConfig.weight.regular,
            ...fontConfig.singleLineSmall,
            css: css`
                &:empty {
                    display: none;
                }
                margin: 8px 0 0 0;
            `,
        },
        css: css`
            & > ${TypographyStyle as any} {
                margin-bottom: 12px;
                &:empty {
                    display: none;
                }
            }
            ${(props: any) => {
                return props.horizontal
                    ? css`
                          & > ${TypographyStyle as any} {
                              margin-bottom: 4px;
                          }
                          display: inline-block;
                          & > ${RadioStyle} {
                              display: inline-block;
                              margin-top: 0px;
                              margin-right: 16px;
                          }
                      `
                    : css`
                          display: flex;
                          flex-direction: column;
                          ${RadioStyle} {
                              display: flex;
                          }
                          ${RadioStyle} + ${RadioStyle} {
                              margin-top: 12px;
                          }
                      `;
            }}
        `,
    },
} as xBaseTheme["radio"];
